import { z } from 'zod';
import { MAX_SIZE_OPTIONS } from './catalog';
import { normalizedUuidSchema as uuidSchema } from './common';
import { quoteQuantitySchema } from './quote-primitives';

// ---------------------------------------------------------------------------
// 상품 사이즈 옵션 — 배송지별 사이즈 줄 계약(spec docs/superpowers/specs/2026-11-02-product-size-options-design.md §5).
// 사이즈 상품은 배송지마다 `sizes` 가 필수이고 합이 배송지 수량과 같아야 한다(서버 유스케이스가 상품 쪽 필수·금지를 본다).
// `shipping.ts` 가 이 파일을 import 하므로 이 파일은 shipping·quotes 를 import 하지 않는다(순환 금지).
// ---------------------------------------------------------------------------

export const DESTINATION_SIZE_DUPLICATE_MESSAGE =
  '한 배송지에 같은 사이즈를 두 번 넣을 수 없습니다.';
export const DESTINATION_SIZE_SUM_MESSAGE = '사이즈별 수량의 합이 배송지 수량과 다릅니다.';
export const DESTINATION_SIZE_EMPTY_MESSAGE = '사이즈를 한 개 이상 넣어 주세요.';

/** 요청 입력의 배송지 사이즈 한 줄 — 사이즈 옵션 id + 수량. */
export const destinationSizeInputSchema = z.strictObject({
  sizeOptionId: uuidSchema,
  quantity: quoteQuantitySchema,
});
export type DestinationSizeInput = z.infer<typeof destinationSizeInputSchema>;

/** 한 배송지의 사이즈 줄 목록 — 1줄 이상, 같은 사이즈 중복 금지. */
export const destinationSizeListSchema = z
  .array(destinationSizeInputSchema)
  .min(1, { message: DESTINATION_SIZE_EMPTY_MESSAGE })
  .max(MAX_SIZE_OPTIONS)
  .refine((sizes) => new Set(sizes.map((size) => size.sizeOptionId)).size === sizes.length, {
    message: DESTINATION_SIZE_DUPLICATE_MESSAGE,
  });

/**
 * 배송지마다 `sizes` 합이 `quantity` 와 같은지 본다 — `sizes` 가 없는 배송지는 건너뛴다. 배송지 스키마 자체에 refine 을 걸면
 * `.omit`·목록 감싸기가 막혀서 네 요청 입력 스키마의 superRefine 이 이 함수를 부른다.
 */
export const refineDestinationSizes = (
  destinations:
    | readonly { quantity: number; sizes?: readonly { quantity: number }[] | undefined }[]
    | undefined,
  ctx: z.RefinementCtx,
): void => {
  destinations?.forEach((destination, index) => {
    if (destination.sizes === undefined) return;
    const sum = destination.sizes.reduce((total, size) => total + size.quantity, 0);
    if (sum !== destination.quantity)
      ctx.addIssue({
        code: 'custom',
        message: DESTINATION_SIZE_SUM_MESSAGE,
        path: ['destinations', index, 'sizes'],
      });
  });
};

/** 출력의 배송지 사이즈 줄 — 이름과 id는 사본이며 FK가 없어 옵션을 지워도 id가 자동으로 null이 되지 않는다. */
export const destinationSizeSchema = z.strictObject({
  sizeOptionId: uuidSchema.nullable(),
  sizeName: z.string(),
  quantity: quoteQuantitySchema,
});
export type DestinationSize = z.infer<typeof destinationSizeSchema>;

/** 견적 전체의 사이즈별 총 수량(비회원 링크 요약 등). */
export const sizeTotalSchema = z.strictObject({
  sizeName: z.string(),
  quantity: quoteQuantitySchema,
});
export type SizeTotal = z.infer<typeof sizeTotalSchema>;

/** 배송지들의 사이즈 줄을 이름별로 합산한다 — 처음 나온 순서를 지킨다. */
export const sizeTotalsOf = (
  destinations: readonly { readonly sizes: readonly { sizeName: string; quantity: number }[] }[],
): SizeTotal[] => {
  const totals = new Map<string, number>();
  for (const destination of destinations)
    for (const size of destination.sizes)
      totals.set(size.sizeName, (totals.get(size.sizeName) ?? 0) + size.quantity);
  return [...totals].map(([sizeName, quantity]) => ({ sizeName, quantity }));
};

/** `M 10 · L 20` — 사이즈 줄이 없으면 빈 문자열. */
export const formatSizeBreakdown = (
  sizes: readonly { sizeName: string; quantity: number }[],
): string =>
  sizes.map((size) => `${size.sizeName} ${size.quantity.toLocaleString('ko-KR')}`).join(' · ');
